import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SignupModal from '../components/SignupModal';
import { Toaster } from '../components/ui/toaster';
import { Sparkles, Zap, TrendingUp, Shield, BarChart3, Users2, Target, Workflow, Bell, Database, Lock, Globe } from 'lucide-react';

const Features = () => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  
  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Lead Scoring',
      description: 'Advanced machine learning algorithms analyze lead behavior, engagement patterns, and firmographic data to automatically score and prioritize your leads.',
      benefits: ['Identify high-intent leads', 'Reduce manual work', 'Improve conversion rates', 'Predictive analytics'],
    },
    {
      icon: Workflow,
      title: 'Automated Workflows',
      description: 'Build sophisticated automation sequences that nurture leads through the funnel based on their actions and engagement levels.',
      benefits: ['Drag-and-drop builder', 'Conditional logic', 'Multi-channel campaigns', 'A/B testing'],
    },
    {
      icon: BarChart3,
      title: 'Real-Time Analytics',
      description: 'Get instant visibility into your lead generation performance with customizable dashboards and real-time reporting.',
      benefits: ['Live dashboards', 'Custom metrics', 'Trend analysis', 'Export capabilities'],
    },
    {
      icon: Users2,
      title: 'Team Collaboration',
      description: 'Work seamlessly with your team using shared workspaces, comments, assignments, and real-time notifications.',
      benefits: ['Shared workspaces', 'Role-based access', 'Activity feeds', 'Team chat'],
    },
    {
      icon: Database,
      title: 'CRM Integration',
      description: 'Sync your leads automatically with Salesforce, HubSpot, Pipedrive, and 50+ other CRM platforms.',
      benefits: ['Two-way sync', '50+ integrations', 'Custom mappings', 'Webhook support'],
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Stay informed with intelligent alerts for hot leads, deal stage changes, and important events.',
      benefits: ['Real-time alerts', 'Custom triggers', 'Multi-channel (email, Slack)', 'Priority flagging'],
    },
    {
      icon: Target,
      title: 'Lead Enrichment',
      description: 'Automatically enrich lead data with company information, social profiles, and contact details from multiple sources.',
      benefits: ['Contact enrichment', 'Company data', 'Social profiles', 'Technology stack'],
    },
    {
      icon: TrendingUp,
      title: 'Revenue Attribution',
      description: 'Track which campaigns and channels drive the most revenue with multi-touch attribution modeling.',
      benefits: ['Multi-touch attribution', 'ROI tracking', 'Channel analysis', 'Custom models'],
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level security with SOC 2 Type II, GDPR, and CCPA compliance. Your data is always protected.',
      benefits: ['SOC 2 Type II', 'GDPR compliant', 'SSO support', 'Data encryption'],
    },
    {
      icon: Globe,
      title: 'Global Scale',
      description: 'Process millions of leads across multiple regions with 99.9% uptime and lightning-fast performance.',
      benefits: ['99.9% uptime', 'Global CDN', 'Auto-scaling', '24/7 monitoring'],
    },
    {
      icon: Lock,
      title: 'Data Privacy',
      description: 'Complete control over your data with advanced privacy features and compliance tools built-in.',
      benefits: ['Data portability', 'Right to be forgotten', 'Consent management', 'Audit logs'],
    },
    {
      icon: Zap,
      title: 'API & Webhooks',
      description: 'Build custom integrations with our RESTful API and real-time webhooks for maximum flexibility.',
      benefits: ['RESTful API', 'Real-time webhooks', 'SDKs available', 'Developer docs'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8">
            <Sparkles className="w-4 h-4" />
            Powerful Features
          </div>
          <h1 className="text-5xl lg:text-7xl font-black mb-8">
            Everything You Need to
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Scale Your Business
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From AI-powered lead scoring to automated workflows, AALIGN gives you all the tools you need to capture, nurture, and convert more leads.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="group p-8 rounded-2xl bg-white border-2 border-gray-100 hover:border-orange-200 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, bidx) => (
                      <li key={bidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-6">
            Ready to Experience These Features?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Start your free 30-day trial and see how AALIGN can transform your lead management.
          </p>
          <button 
            onClick={() => setIsSignupModalOpen(true)}
            className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-12 py-4 rounded-xl shadow-2xl transition-all transform hover:scale-105"
          >
            Start Free Trial
          </button>
        </div>
      </section>

      <Footer />
      
      {/* Signup Modal */}
      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
    </div>
  );
};

export default Features;